import React from 'react';
import { Button } from '@/components/ui/button';
import { Calendar, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { analytics } from '@/lib/analytics';

interface ScheduleButtonProps {
  locale?: 'en' | 'es';
  variant?: 'default' | 'secondary' | 'link' | 'outline' | 'destructive' | 'ghost';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  source: string;
  intent?: string;
  className?: string;
  children?: React.ReactNode;
  disabled?: boolean;
}

export const ScheduleButton: React.FC<ScheduleButtonProps> = ({
  locale = 'en',
  variant = 'default',
  size = 'default',
  source,
  intent = 'demo',
  className,
  children,
  disabled = false
}) => {
  const navigate = useNavigate();
  const [isRouting, setIsRouting] = React.useState(false);

  // Compute canonical target based on locale and fallbacks
  const getTargetUrl = () => {
    // Check if /schedule route exists (it doesn't currently)
    const hasSchedulePage = false; // Would check routes dynamically
    
    if (hasSchedulePage) {
      return locale === 'es' ? '/es/schedule#top' : '/schedule#top';
    }
    
    // Fallback to contact page with intent
    const contactUrl = '/contact';
    return `${contactUrl}?intent=${intent}&source=${source}#top`;
  };

  // Get button text based on locale and intent
  const getButtonText = () => {
    if (children) return children;
    
    const texts = {
      en: {
        demo: 'Request Free Demo',
        consultation: 'Book Consultation',
        diagnostic: 'Book Free Diagnostic',
        schedule: 'Schedule Now'
      },
      es: {
        demo: 'Solicitar Demo Gratuito',
        consultation: 'Reservar Consulta',
        diagnostic: 'Agenda Diagnóstico Gratuito',
        schedule: 'Agendar Ahora'
      }
    };
    
    return texts[locale][intent as keyof typeof texts.en] || texts[locale].demo;
  };

  const handleClick = async () => {
    if (disabled || isRouting) return;
    
    setIsRouting(true);
    
    try {
      // Track analytics event
      analytics.track('schedule_click', {
        locale,
        page: window.location.pathname,
        section: source,
        source,
        intent
      });

      const targetUrl = getTargetUrl();
      console.log('Navigating to:', targetUrl);
      
      // Navigate directly
      navigate(targetUrl);
      
      // Track successful navigation after a brief delay
      setTimeout(() => {
        analytics.track('schedule_loaded', {
          locale,
          target: targetUrl,
          source
        });
      }, 100);
        
    } catch (error) {
      console.error('Schedule button error:', error);
      // Fallback to mailto if navigation completely fails
      const subject = encodeURIComponent('PriceZing Demo Request');
      const body = encodeURIComponent(`From: ${window.location.href}\nIntent: ${intent}\nSource: ${source}`);
      const mailtoUrl = `mailto:info@pricezing.co?subject=${subject}&body=${body}`;
      
      window.location.href = mailtoUrl;
      
      analytics.track('schedule_fallback_used', {
        locale,
        source,
        method: 'mailto'
      });
    } finally {
      setIsRouting(false);
    }
  };

  return (
    <Button
      variant={variant}
      size={size}
      onClick={handleClick}
      disabled={disabled || isRouting}
      className={cn(
        // Ensure proper styling based on variant
        variant === 'default' && 'bg-orange-500 hover:bg-orange-600 text-white shadow-strong',
        variant === 'secondary' && 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        variant === 'outline' && 'border-2 hover:bg-accent hover:text-accent-foreground',
        'transition-all duration-300 group',
        isRouting && 'opacity-50 cursor-not-allowed',
        className
      )}
      aria-label={`${getButtonText()} - ${locale === 'en' ? 'Opens scheduling flow' : 'Abrir flujo de agendamiento'}`}
    >
      <Calendar className="w-4 h-4 mr-2 group-hover:scale-110 transition-transform" />
      {getButtonText()}
      <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
    </Button>
  );
};